import React, { useState, useEffect, useCallback } from "react";
import { useWeb3 } from "../hooks/useWeb3";
import {
  formatEth,
  parseEth,
  formatAddress,
  getMinimumEthForUSD,
} from "../utils/contractUtils";
import "./FundMe.css";

const FundMe: React.FC = () => {
  const {
    contract,
    account,
    isConnected,
    isCorrectNetwork,
    connectWallet,
    switchToZkSyncSepolia,
  } = useWeb3();

  const [donationAmount, setDonationAmount] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalFunded, setTotalFunded] = useState<string>("0");
  const [userContribution, setUserContribution] = useState<string>("0");
  const [minEthRequired, setMinEthRequired] = useState<string>("0.0004");
  const [isOwner, setIsOwner] = useState<boolean>(false);

  const fetchContractData = useCallback(async () => {
    if (!contract || !account) return;

    try {
      const userFunded = await contract.addressToAmountFunded(account);
      setUserContribution(formatEth(userFunded));

      // Get total contract balance
      const balance = await contract.runner?.provider?.getBalance(
        contract.target
      );
      if (balance) {
        setTotalFunded(formatEth(balance));
      }
    } catch (error) {
      console.error("Error fetching contract data:", error);
    }
  }, [contract, account]);

  const checkIfOwner = useCallback(async () => {
    if (!contract || !account) return;

    try {
      const owner = await contract.i_owner();
      setIsOwner(owner.toLowerCase() === account.toLowerCase());
    } catch (error) {
      console.error("Error checking owner:", error);
    }
  }, [contract, account]);

  const calculateMinEth = useCallback(async () => {
    if (!contract) return;

    try {
      const minEth = await getMinimumEthForUSD(contract, 1);
      setMinEthRequired(minEth);
    } catch (error) {
      console.error("Error calculating min ETH:", error);
    }
  }, [contract]);

  const handleDonate = async () => {
    if (!contract || !donationAmount) return;

    setIsLoading(true);
    try {
      const tx = await contract.fund({
        value: parseEth(donationAmount),
      });

      await tx.wait();
      alert("Donation successful! Thank you for your support!");
      setDonationAmount("");
      fetchContractData();
    } catch (error: any) {
      console.error("Error donating:", error);
      if (error.message.includes("didn't send enough ETH")) {
        alert(
          `Minimum donation is $1 USD (approximately ${minEthRequired} ETH)`
        );
      } else {
        alert("Donation failed. Please try again.");
      }
    }
    setIsLoading(false);
  };

  const handleWithdraw = async () => {
    if (!contract) return;

    setIsLoading(true);
    try {
      const tx = await contract.withdraw();
      await tx.wait();
      alert("Withdrawal successful!");
      fetchContractData();
    } catch (error) {
      console.error("Error withdrawing:", error);
      alert("Withdrawal failed. Please try again.");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (contract && account) {
      fetchContractData();
      checkIfOwner();
      calculateMinEth();
    }
  }, [contract, account, fetchContractData, checkIfOwner, calculateMinEth]);

  if (!isConnected) {
    return (
      <div className="fundme-container">
        <div className="connect-wallet">
          <h1>Solidity Project: FundMe(1USD~)</h1>
          <p>지갑을 연결하시면 1USD부터 기부할 수 있습니다!</p>
          <button onClick={connectWallet} className="connect-btn">
            Connect Wallet
          </button>
        </div>
      </div>
    );
  }

  if (!isCorrectNetwork) {
    return (
      <div className="fundme-container">
        <div className="wrong-network">
          <h1>Wrong Network</h1>
          <p>Please switch to zkSync Sepolia Testnet</p>
          <button onClick={switchToZkSyncSepolia} className="switch-btn">
            Switch to zkSync Sepolia
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fundme-container">
      <header className="header">
        <h1>FundMe - If you'd Like!</h1>
        <div className="wallet-info">
          <span>Connected: {formatAddress(account)}</span>
        </div>
      </header>

      <div className="stats">
        <div className="stat-item">
          <h3>Total Raised</h3>
          <p>{totalFunded} ETH</p>
        </div>
        <div className="stat-item">
          <h3>Your Contribution</h3>
          <p>{userContribution} ETH</p>
        </div>
        <div className="stat-item">
          <h3>Minimum Donation</h3>
          <p>$1 USD (~{minEthRequired} ETH)</p>
        </div>
      </div>

      <div className="donation-form">
        <h2>Make a Donation</h2>
        <p>Help support our project with a minimum donation of $1 USD</p>
        <p>최소 $1 USD부터 기부하실 수 있습니다.</p>
        <p>
          솔리디티 개인 프로젝트로 만들었습니다. ZkSync Sepolia Testnet에
          배포되었습니다.
        </p>

        <div className="input-group">
          <label htmlFor="amount">Donation Amount (ETH)</label>
          <input
            type="number"
            id="amount"
            placeholder={`Minimum: ${minEthRequired} ETH`}
            value={donationAmount}
            onChange={(e) => setDonationAmount(e.target.value)}
            step="0.0001"
            min={minEthRequired}
          />
        </div>

        <button
          onClick={handleDonate}
          disabled={
            isLoading ||
            !donationAmount ||
            parseFloat(donationAmount) < parseFloat(minEthRequired)
          }
          className="donate-btn"
        >
          {isLoading ? "Processing..." : "Donate Now"}
        </button>

        {parseFloat(donationAmount || "0") > 0 &&
          parseFloat(donationAmount) < parseFloat(minEthRequired) && (
            <p className="error">
              Minimum donation is ${1} USD (approximately {minEthRequired} ETH)
            </p>
          )}
      </div>

      {isOwner && (
        <div className="owner-section">
          <h2>Owner Functions</h2>
          <button
            onClick={handleWithdraw}
            disabled={isLoading}
            className="withdraw-btn"
          >
            {isLoading ? "Processing..." : "Withdraw Funds"}
          </button>
        </div>
      )}

      <footer className="footer">
        <p>
          Contract Address:{" "}
          <a
            href="https://sepolia.explorer.zksync.io/address/0xE52fF508a386c4708F0EaC0aF339f2147BAeF910"
            target="_blank"
            rel="noopener noreferrer"
          >
            {formatAddress("0xE52fF508a386c4708F0EaC0aF339f2147BAeF910")}
          </a>
        </p>
      </footer>
    </div>
  );
};

export default FundMe;
